/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * QRCambioDOS — Passaggio consegna DOS tramite QR Code
 *
 * Il DOS uscente genera un QR Code che contiene tutti i dati
 * operativi compressi (LZ-string). Il DOS entrante scansiona
 * il QR con il Samsung Tab Active5 Pro e DOS518 si aggiorna
 * automaticamente con la situazione in corso.
 *
 * Flusso:
 *   DOS uscente → "Genera QR" → mostra QR sullo schermo
 *   DOS entrante → scansiona con camera → apre URL dos518://...
 *   DOS518 riconosce l'URL → importa dati → registra cambio nel diario
 *
 * Dati inclusi nel QR (payload minimo, ottimizzato per dimensione):
 *   - Scheda COAU (comune, coordinate, codice)
 *   - Personale attivo
 *   - Mezzi terrestri e aerei
 *   - Ultime 15 voci del diario operativo
 *   - Nome DOS uscente + nota briefing
 */
// src/components/QR/QRCambioDOS.tsx
import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import LZString from 'lz-string';
import { useAppStore } from '../../store/useAppStore';

// ================================================================
// PAYLOAD QR — struttura minima per il passaggio consegna
// ================================================================
interface PayloadQR {
  v: 1;                           // versione schema
  t: string;                      // timestamp generazione
  dos: string;                    // DOS uscente
  briefing?: string;              // note briefing
  coau?: Record<string, any>;
  personale?: any[];
  mezziT?: any[];
  mezziA?: any[];
  droni?: any[];
  diario?: any[];                 // ultime 15 voci
}

function buildPayload(briefing: string): PayloadQR {
  const s = useAppStore.getState();
  return {
    v: 1,
    t: new Date().toISOString(),
    dos: s.dosAttivo || '',
    briefing: briefing || undefined,
    coau: s.coau ? {
      comune:            (s.coau as any).comune || (s.coau as any).localizzazione,
      latitudine:        (s.coau as any).latitudine,
      longitudine:       (s.coau as any).longitudine,
      codiceIntervento:  (s.coau as any).codiceIntervento,
      localita:          (s.coau as any).localita,
    } : undefined,
    personale: s.personale.map(p => ({
      n: p.nome, c: p.cognome, q: p.qualifica, t: p.telefono,
    })),
    mezziT: s.mezziTerrestri.map(m => ({
      s: m.sigla, t: m.tipo, e: (m as any).ente,
    })),
    mezziA: s.mezziAerei.map(m => ({
      s: m.sigla, t: m.tipo, mo: (m as any).modello,
    })),
    droni: s.droni.map(d => ({
      mo: (d as any).modello, op: d.operatore,
      cr: (d as any).coordsRilevate, or: (d as any).oraRilevamento,
    })),
    // Ultime 15 voci diario per il briefing
    diario: (s.diarioOperativo || []).slice(-15).map(v => ({
      o: v.ora, ic: v.icona, tx: v.testo, tp: v.tipo,
    })),
  };
}

// Comprimi payload → stringa URL-safe
function comprimiPayload(payload: PayloadQR): string {
  const json = JSON.stringify(payload);
  return LZString.compressToEncodedURIComponent(json);
}

// URL che DOS518 riconoscerà al lancio / scansione
// Usa il parametro ?d518= sull'URL corrente della PWA
function buildQRUrl(compressed: string): string {
  const base = window.location.origin + window.location.pathname;
  return `${base}?d518=${compressed}`;
}

// Decodifica payload da URL (usato al lancio app)
export function decodificaPayloadDaURL(): PayloadQR | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const d518 = params.get('d518');
    if (!d518) return null;
    const json = LZString.decompressFromEncodedURIComponent(d518);
    if (!json) return null;
    return JSON.parse(json) as PayloadQR;
  } catch {
    return null;
  }
}

// Importa payload nello store
export function importaPayloadQR(payload: PayloadQR): void {
  const { cambioDOS, addVoceDiario } = useAppStore.getState();

  // Ricostruisce dati compatibili con lo store
  useAppStore.setState((s) => ({
    coau: payload.coau ? {
      ...(s.coau || {}),
      ...payload.coau,
      id: (s.coau as any)?.id || 'coau-qr',
      data: payload.t,
    } as any : s.coau,
    personale: payload.personale?.map((p, i) => ({
      id: `p-qr-${i}`,
      nome: p.n || '', cognome: p.c || '',
      qualifica: p.q || 'operatore',
      telefono: p.t || '',
    })) ?? s.personale,
    mezziTerrestri: payload.mezziT?.map((m, i) => ({
      id: `mt-qr-${i}`,
      sigla: m.s || '', tipo: m.t || 'altro',
      ente: m.e || '',
      posizioneOperativa: 'disponibile',
    })) ?? s.mezziTerrestri,
    mezziAerei: payload.mezziA?.map((m, i) => ({
      id: `ma-qr-${i}`,
      sigla: m.s || '', tipo: m.t || 'ala_rotante',
      modello: m.mo || '',
      posizioneOperativa: 'disponibile',
    })) ?? s.mezziAerei,
    droni: payload.droni?.map((d, i) => ({
      id: `dr-qr-${i}`,
      modello: d.mo || '', operatore: d.op || '',
      coordsRilevate: d.cr || '',
      oraRilevamento: d.or || '',
      posizioneOperativa: 'disponibile' as const,
    })) ?? s.droni,
  }));

  // Registra cambio DOS nel diario
  const nuovoDos = useAppStore.getState().dosAttivo || '';
  cambioDOS(nuovoDos, payload.briefing);

  // Log ricezione QR
  addVoceDiario(
    'cambio_dos', '📲',
    `Dati importati via QR Code — DOS uscente: ${payload.dos}${payload.briefing ? ` | ${payload.briefing}` : ''}`,
    { fonte: 'QR', dosUscente: payload.dos }
  );

  // Pulisce URL (rimuove parametro ?d518=)
  const url = window.location.pathname;
  window.history.replaceState({}, '', url);
}

// ================================================================
// COMPONENTE
// ================================================================

export const QRCambioDOS: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const dosAttivo     = useAppStore((s) => s.dosAttivo);
  const personale     = useAppStore((s) => s.personale);
  const mezziTerrestri= useAppStore((s) => s.mezziTerrestri);
  const mezziAerei    = useAppStore((s) => s.mezziAerei);
  const droni         = useAppStore((s) => s.droni);
  const diario        = useAppStore((s) => s.diarioOperativo);
  const cambioDOS     = useAppStore((s) => s.cambioDOS);

  const [tab, setTab]             = useState<'genera' | 'scansiona'>('genera');
  const [briefing, setBriefing]   = useState('');
  const [nuovoDos, setNuovoDos]   = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrTxt, setQrTxt]         = useState('');
  const [generando, setGenerando] = useState(false);
  const [dimensione, setDimensione] = useState(0);
  const [errore, setErrore]       = useState('');
  // Scan manuale (paste testo QR)
  const [qrInput, setQrInput]     = useState('');
  const [importOK, setImportOK]   = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handleGenera = async () => {
    setGenerando(true);
    setErrore('');
    try {
      const payload = buildPayload(briefing);
      const compressed = comprimiPayload(payload);
      const url = buildQRUrl(compressed);
      setDimensione(compressed.length);
      setQrTxt(url);

      const dataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 320,
        color: { dark: '#0f172a', light: '#f8fafc' },
      });
      setQrDataUrl(dataUrl);
    } catch (err: any) {
      setErrore('Errore generazione QR: ' + (err.message || err));
    } finally {
      setGenerando(false);
    }
  };

  // Genera automaticamente al mount
  useEffect(() => { handleGenera(); }, []);

  const handleImportaTesto = () => {
    try {
      // Estrae ?d518= dall'URL incollato o dalla stringa compressa diretta
      let compressed = qrInput.trim();
      if (compressed.includes('?d518=')) {
        compressed = compressed.split('?d518=')[1];
      }
      const json = LZString.decompressFromEncodedURIComponent(compressed);
      if (!json) throw new Error('Decompressione fallita');
      const payload = JSON.parse(json) as PayloadQR;

      if (!nuovoDos.trim()) {
        setErrore('Inserisci il nome del DOS entrante');
        return;
      }

      // Aggiorna dosAttivo prima di importare
      useAppStore.setState({ dosAttivo: nuovoDos.trim() });
      importaPayloadQR(payload);
      setImportOK(true);
    } catch {
      setErrore('QR non valido — assicurati di copiare l\'intero URL');
    }
  };

  // Stima leggibilità QR
  const qrLeggibile = dimensione < 2500;
  const qrWarning   = dimensione >= 2000 && dimensione < 2500;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000, padding: '16px', overflowY: 'auto',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
        border: '2px solid #ec4899',
        borderRadius: '16px', width: '100%', maxWidth: '520px',
        boxShadow: '0 20px 60px rgba(236,72,153,0.3)',
      }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #831843, #be185d)',
          borderRadius: '14px 14px 0 0', padding: '18px 22px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.2em' }}>
              📲 QR Code — Cambio DOS
            </h2>
            <small style={{ color: '#fbcfe8' }}>Passaggio consegna istantaneo via scansione</small>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
            width: '34px', height: '34px', borderRadius: '50%', cursor: 'pointer', fontSize: '1.1rem',
          }}>✕</button>
        </div>

        {/* Tab */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1e293b' }}>
          {(['genera', 'scansiona'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              flex: 1, padding: '12px', border: 'none', cursor: 'pointer',
              background: tab === t ? '#1e293b' : 'transparent',
              color: tab === t ? '#f9a8d4' : '#64748b',
              fontWeight: tab === t ? 700 : 400,
              borderBottom: tab === t ? '2px solid #ec4899' : '2px solid transparent',
              fontSize: '0.9rem',
            }}>
              {t === 'genera' ? '📤 DOS Uscente — Genera QR' : '📥 DOS Entrante — Importa'}
            </button>
          ))}
        </div>

        <div style={{ padding: '20px' }}>

          {/* ── TAB GENERA ── */}
          {tab === 'genera' && (
            <>
              {/* Info dati inclusi */}
              <div style={{
                display: 'flex', gap: '8px', flexWrap: 'wrap',
                marginBottom: '14px',
              }}>
                {[
                  { label: `${personale.length} operatori`, ok: personale.length > 0 },
                  { label: `${mezziTerrestri.length} mezzi T.`, ok: mezziTerrestri.length > 0 },
                  { label: `${mezziAerei.length} mezzi A.`, ok: mezziAerei.length > 0 },
                  { label: `${droni.length} droni`, ok: droni.length > 0 },
                  { label: `${Math.min((diario||[]).length, 15)} voci diario`, ok: (diario||[]).length > 0 },
                ].map(({ label, ok }) => (
                  <span key={label} style={{
                    background: ok ? '#1e3a5f' : '#1e293b',
                    color: ok ? '#93c5fd' : '#475569',
                    border: `1px solid ${ok ? '#2563eb' : '#334155'}`,
                    borderRadius: '20px', padding: '3px 10px', fontSize: '0.75rem',
                  }}>
                    {label}
                  </span>
                ))}
              </div>

              {/* Briefing */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                  Note briefing per DOS entrante (opzionale)
                </label>
                <textarea
                  rows={2}
                  placeholder="Es: Fronte nord contenuto, attenzione vento NE, squadra 2 in posizione..."
                  value={briefing}
                  onChange={(e) => setBriefing(e.target.value)}
                  onBlur={handleGenera}
                  style={{
                    width: '100%', boxSizing: 'border-box', marginTop: '5px',
                    background: '#1e293b', color: '#f1f5f9',
                    border: '1px solid #334155', borderRadius: '6px',
                    padding: '8px 10px', fontSize: '0.85rem', resize: 'vertical',
                  }}
                />
              </div>

              {/* QR Code */}
              {qrDataUrl && (
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <div style={{
                    display: 'inline-block',
                    background: '#f8fafc', padding: '12px', borderRadius: '12px',
                    border: `3px solid ${qrWarning ? '#f97316' : qrLeggibile ? '#22c55e' : '#dc2626'}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                  }}>
                    <img src={qrDataUrl} alt="QR Code Cambio DOS" style={{ width: 280, height: 280, display: 'block' }} />
                  </div>

                  <div style={{ marginTop: '10px' }}>
                    <span style={{
                      color: qrWarning ? '#fbbf24' : qrLeggibile ? '#22c55e' : '#ef4444',
                      fontSize: '0.8rem', fontWeight: 700,
                    }}>
                      {qrLeggibile
                        ? `✅ QR leggibile (${dimensione} char)${qrWarning ? ' — usa buona luce' : ''}`
                        : `⚠️ Dati troppo grandi (${dimensione} char) — semplifica`}
                    </span>
                  </div>
                </div>
              )}

              {generando && (
                <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
                  ⏳ Generazione QR...
                </div>
              )}

              {errore && (
                <div style={{ color: '#fca5a5', background: '#450a0a', border: '1px solid #dc2626', borderRadius: '6px', padding: '10px', marginBottom: '10px', fontSize: '0.85rem' }}>
                  ⚠️ {errore}
                </div>
              )}

              <button
                onClick={handleGenera}
                disabled={generando}
                style={{
                  width: '100%', background: 'linear-gradient(135deg, #831843, #be185d)',
                  color: '#fff', border: 'none', borderRadius: '8px',
                  padding: '10px', cursor: 'pointer', fontWeight: 700,
                }}
              >
                🔄 Rigenera QR
              </button>

              <p style={{ color: '#475569', fontSize: '0.75rem', marginTop: '12px', textAlign: 'center' }}>
                Mostra questo schermo al DOS entrante per la scansione.<br/>
                Il QR si aggiorna automaticamente se modifichi il briefing.
              </p>
            </>
          )}

          {/* ── TAB IMPORTA ── */}
          {tab === 'scansiona' && !importOK && (
            <>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '16px' }}>
                Scansiona il QR con la camera del tablet. Se DOS518 si apre automaticamente,
                i dati vengono importati. In alternativa copia l'URL dalla barra del browser
                e incollalo qui sotto.
              </p>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                  Il tuo nome — DOS entrante *
                </label>
                <input
                  type="text"
                  placeholder="Nome e Cognome"
                  value={nuovoDos}
                  onChange={(e) => setNuovoDos(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%', boxSizing: 'border-box', marginTop: '5px',
                    background: '#1e293b', color: '#f1f5f9',
                    border: '1px solid #ec4899', borderRadius: '6px',
                    padding: '8px 12px', fontSize: '0.95rem',
                  }}
                />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                  Incolla URL scansionato (o stringa compressa)
                </label>
                <textarea
                  rows={4}
                  placeholder="https://...?d518=... oppure solo il codice dopo ?d518="
                  value={qrInput}
                  onChange={(e) => setQrInput(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box', marginTop: '5px',
                    background: '#1e293b', color: '#f1f5f9',
                    border: '1px solid #334155', borderRadius: '6px',
                    padding: '8px 10px', fontSize: '0.8rem',
                    fontFamily: 'monospace', resize: 'vertical',
                  }}
                />
              </div>

              {errore && (
                <div style={{ color: '#fca5a5', background: '#450a0a', border: '1px solid #dc2626', borderRadius: '6px', padding: '10px', marginBottom: '10px', fontSize: '0.85rem' }}>
                  ⚠️ {errore}
                </div>
              )}

              <button
                onClick={handleImportaTesto}
                disabled={!qrInput.trim() || !nuovoDos.trim()}
                style={{
                  width: '100%',
                  background: qrInput.trim() && nuovoDos.trim()
                    ? 'linear-gradient(135deg, #166534, #22c55e)'
                    : '#334155',
                  color: qrInput.trim() && nuovoDos.trim() ? '#fff' : '#64748b',
                  border: 'none', borderRadius: '8px',
                  padding: '12px', cursor: qrInput.trim() && nuovoDos.trim() ? 'pointer' : 'default',
                  fontWeight: 700, fontSize: '1rem',
                }}
              >
                📥 Importa dati operazione
              </button>
            </>
          )}

          {/* ── IMPORTAZIONE OK ── */}
          {tab === 'scansiona' && importOK && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '4rem', marginBottom: '12px' }}>✅</div>
              <h3 style={{ color: '#22c55e', margin: '0 0 8px' }}>Dati importati</h3>
              <p style={{ color: '#94a3b8', marginBottom: '6px' }}>
                DOS: <strong style={{ color: '#f1f5f9' }}>{nuovoDos}</strong>
              </p>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '24px' }}>
                Personale, mezzi e diario caricati dal QR Code.<br/>
                Il cambio DOS è stato registrato nel Diario Operativo.
              </p>
              <button
                onClick={onClose}
                style={{
                  background: 'linear-gradient(135deg, #831843, #be185d)',
                  color: '#fff', border: 'none', borderRadius: '8px',
                  padding: '12px 32px', cursor: 'pointer', fontWeight: 700, fontSize: '1rem',
                }}
              >
                Vai a DOS518 →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
